/**
 * Timezone-safe date/time helpers.
 *
 * Mirrors american-drone-network-web/src/app/shared/utils/date.utils.ts — keep
 * the two in sync.
 *
 * Two Postgres column types reach the browser as ISO strings that MUST NOT be
 * run through `new Date(...).toLocaleDateString()`:
 *
 *  1. `date` columns (application_date, expiration_date, …). The Hub runs in
 *     UTC, so these serialize as `"2026-07-29T00:00:00.000Z"`. Rendering that
 *     in any US timezone yields the *previous* day ("Jul 28, 2026").
 *
 *  2. `timestamp without time zone` columns (drone flight start_time/end_time).
 *     These store the literal wall-clock the pilot typed. They serialize with a
 *     `Z` suffix they never earned, so converting them shifts the clock.
 *
 * In both cases the stored value is a *literal* the user typed, not an instant
 * in time — so we read the literal back out instead of converting it.
 */

/**
 * A calendar date with no meaningful time component:
 *   "2026-07-29"
 *   "2026-07-29T00:00:00.000Z"  ← Postgres `date` column, UTC server
 */
const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?Z?)?$/;

/** Time-of-day embedded in an ISO timestamp: "…T14:10:00" → 14:10 */
const TIMESTAMP_TIME_RE = /T(\d{2}):(\d{2})/;

/** A bare clock time as typed into `<input type="time">`: "14:10" (seconds optional) */
const BARE_TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

/**
 * Parse a calendar-date string into a Date at LOCAL midnight, so formatting it
 * can never roll the day backwards. Returns null if the value carries a real
 * time component (i.e. it's a genuine instant, not a calendar date).
 */
export function parseCalendarDate(value: string): Date | null {
  const match = CALENDAR_DATE_RE.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Reject invalid calendar dates that JS would silently roll over
  // (e.g. "2026-02-31" → Mar 3): require the parts to round-trip.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * A calendar date as an `<input type="date">` value ("2026-07-29"), or '' when the value isn't one.
 * Reads the literal date, so a `date` column's "2026-07-29T00:00:00.000Z" stays the 29th.
 */
export function toCalendarDateInput(value: string | null | undefined): string {
  const date = value ? parseCalendarDate(value) : null;
  return date ? toCalendarDateString(date) : '';
}

/** A local Date's calendar day as "YYYY-MM-DD" (today's date with `new Date()`). */
export function toCalendarDateString(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Normalize any date-ish value into a Date safe to format for *day* display.
 * Calendar dates are pinned to local midnight; real timestamps pass through.
 */
export function toDisplayDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const calendar = parseCalendarDate(value);
  if (calendar) return calendar;

  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Format a calendar date for display without timezone drift.
 *
 * @returns the formatted date, or `fallback` when there is nothing to show.
 */
export function formatCalendarDate(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
  fallback = '-'
): string {
  const date = toDisplayDate(value);
  if (!date) return fallback;
  return date.toLocaleDateString('en-US', options);
}

/**
 * Extract the wall-clock time a flight was logged with and format it 12-hour.
 *
 * Accepts either an ISO timestamp ("2026-07-29T10:00:00.000Z") or a bare time
 * ("10:00"). The time is read literally — never timezone-converted — because
 * `start_time`/`end_time` are `timestamp without time zone` columns holding
 * exactly what the pilot typed.
 *
 * @returns "10:00 AM", or `fallback` when there is nothing to show.
 */
export function formatClockTime(
  value: string | Date | null | undefined,
  fallback = 'N/A'
): string {
  if (!value) return fallback;

  let hours: number;
  let minutes: string;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return fallback;
    hours = value.getHours();
    minutes = value.getMinutes().toString().padStart(2, '0');
  } else {
    const match = TIMESTAMP_TIME_RE.exec(value) ?? BARE_TIME_RE.exec(value);
    if (!match) return fallback;
    hours = Number(match[1]);
    minutes = match[2];
  }

  if (!Number.isFinite(hours) || hours < 0 || hours > 23) return fallback;

  const minutesNum = Number(minutes);
  if (!Number.isFinite(minutesNum) || minutesNum < 0 || minutesNum > 59) return fallback;

  const meridiem = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes} ${meridiem}`;
}
